import { useDebugStore } from '@client/stores/DebugStore';
import { getFlag, getFlagSync, initializePostHog } from '@client/utils/feature-flags';
import { useFeatureFlag } from '../useFeatureFlag';

/**
 * The PostHog flag half of the UI debug-info surface. Named once so the reactive
 * and the imperative reader cannot drift onto different keys.
 */
export const UI_DEBUG_INFO_FLAG_KEY = 'enable-debug-info';

/**
 * The debug-info predicate itself: the debug-panel toggle AND the PostHog flag.
 * Both readers below evaluate THIS function, so a component that hides a control
 * and a `beforeLoad` that redirects away from it can never disagree about what
 * "debug info is enabled" means.
 */
function isUIDebugInfoEnabled(toggle: boolean, flag: boolean): boolean {
    return toggle === true && flag === true;
}

/**
 * Returns whether UI component debug info should be displayed.
 * Controlled by the "UI debug info" checkbox in the debug panel.
 */
export const useIsUIDebugInfoEnabled = (): boolean => {
    const { isUIDebugInfoEnabledFlag } = useDebugStore();
    const isDebugInfoEnabled = useFeatureFlag(UI_DEBUG_INFO_FLAG_KEY, false);
    return isUIDebugInfoEnabled(isUIDebugInfoEnabledFlag, isDebugInfoEnabled);
};

/**
 * CUR-778 — the same gate, read imperatively.
 *
 * TanStack Router's `beforeLoad` runs outside React and cannot call hooks, but a
 * route guard that hides nothing and a button that hides everything would be two
 * different gates. This reads the identical two inputs — the persisted debug-panel
 * toggle and the `enable-debug-info` flag — through their non-React accessors and
 * applies the identical predicate, so "the button is hidden" and "the URL
 * redirects" are the same condition by construction.
 *
 * Async because PostHog readiness is part of the gate. On a hard load — refresh,
 * new tab, pasted URL — a route guard runs before `initializePostHog()` resolves,
 * and until then EVERY flag reader (`getFlag` exactly as much as `getFlagSync`)
 * answers with the caller's default, because `enable-debug-info` is not in the
 * anonymous server bootstrap. Reading synchronously would therefore see `false`
 * for a debug-enabled user and redirect them off their own URL, while the button
 * that opened it stayed visible on the page behind. Awaiting the cached init
 * promise (a no-op once resolved) is what keeps the two readers in agreement, and
 * it lives HERE rather than in the guard so a future caller cannot forget it.
 */
export async function readIsUIDebugInfoEnabled(): Promise<boolean> {
    await initializePostHog();
    return isUIDebugInfoEnabled(
        useDebugStore.getState().isUIDebugInfoEnabledFlag,
        await getFlag(UI_DEBUG_INFO_FLAG_KEY, false),
    );
}

/**
 * CUR-1679 — the same gate, read synchronously: whether the tRPC link sends
 * `x-debug-info` on a request made now, and so whether debug-only fields (the
 * compliance-audit fallback marks) come back at all. The link's header builder
 * in `client/src/trpc/client.tsx` reads the identical inputs — the persisted
 * toggle and `getFlagSync('enable-debug-info')` — and its
 * `(flag || force) && (toggle && flag)` reduces to this predicate.
 *
 * Unlike {@link readIsUIDebugInfoEnabled} it does not wait for PostHog: a request
 * cannot be held back for flags, so until initialisation the flag reads as its
 * bootstrap value or `false`. `useSyncQueriesWithUIDebugInfo` re-reads THIS
 * function and refetches when its answer changes, so a response cached under the
 * earlier answer does not outlive it.
 */
export function readIsUIDebugInfoEnabledSync(): boolean {
    return isUIDebugInfoEnabled(
        useDebugStore.getState().isUIDebugInfoEnabledFlag,
        getFlagSync(UI_DEBUG_INFO_FLAG_KEY, false),
    );
}
